/*
  Blocks search forms from being submitted with an empty (or whitespace-only) query, or one
  shorter than MIN_QUERY_LENGTH characters.

  Every storefront search form (header bar, search drawer, /search page, 404...) posts a
  `q` field to routes.search_url; an empty submit lands on /search?q= and, with Algolia on,
  is logged as an "Empty Search". Instead of navigating we keep the user on the field and
  turn its placeholder into the error message, so no extra element has to fit in the
  (very different) layouts those forms live in. Runs in the capture phase so it wins over
  any other submit handler. Independent from settings.algolia_enabled.

  The same threshold applies to the live results of the search drawer: below
  MIN_QUERY_LENGTH characters it keeps showing its empty-input state (suggestions menu)
  instead of querying Shopify/Algolia. Its debounce is also raised to SEARCH_DEBOUNCE_MS.
*/
(function () {
  "use strict";

  var MIN_QUERY_LENGTH = 2;
  // theme.js uses 200ms: long enough only for fast typists, so most keystrokes hit Algolia
  // (billed per request, aborted ones included). 350ms waits for an actual pause.
  var SEARCH_DEBOUNCE_MS = 350;
  var ERROR_CLASS = "is-search-empty-error";
  var message = (window.themeVariables && window.themeVariables.strings.searchEmptyQuery) || "";
  var liveRegion = null;

  function announce(text) {
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.className = "visually-hidden";
      liveRegion.setAttribute("role", "alert");
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    // New text on a separate frame so screen readers re-announce repeated attempts
    requestAnimationFrame(function () { liveRegion.textContent = text; });
  }

  function clearError(input) {
    if (!input.classList.contains(ERROR_CLASS)) return;
    input.classList.remove(ERROR_CLASS);
    input.removeAttribute("aria-invalid");
    if (input.hasAttribute("data-original-placeholder")) {
      input.placeholder = input.getAttribute("data-original-placeholder");
      input.removeAttribute("data-original-placeholder");
    }
  }

  function showError(input) {
    if (!input.hasAttribute("data-original-placeholder")) {
      input.setAttribute("data-original-placeholder", input.placeholder || "");
    }
    input.value = "";
    input.placeholder = message;
    input.setAttribute("aria-invalid", "true");
    // Restart the shake animation on repeated attempts
    input.classList.remove(ERROR_CLASS);
    void input.offsetWidth;
    input.classList.add(ERROR_CLASS);
    input.focus();
    announce(message);
  }

  function isSearchForm(form) {
    try {
      return /\/search\/?$/.test(new URL(form.action, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !isSearchForm(form)) return;

    // form.elements also picks up inputs associated via the form="" attribute
    var input = form.elements.namedItem("q");
    if (!input || input instanceof RadioNodeList || input.value.trim().length >= MIN_QUERY_LENGTH) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showError(input);
  }, true);

  document.addEventListener("input", function (event) {
    if (event.target instanceof HTMLInputElement && event.target.name === "q") {
      clearError(event.target);
    }
  });

  document.addEventListener("focusout", function (event) {
    if (event.target instanceof HTMLInputElement && event.target.name === "q") {
      clearError(event.target);
    }
  });

  // predictive-search-drawer (theme.js, generated bundle: not edited) binds its debounced
  // _onSearch to `input` on [name="q"] in connectedCallback and reads target.value when the
  // debounce fires. We swap that listener for one that hands _onSearch an empty value while
  // the query is too short, which is exactly its empty-input branch (menu list shown, results
  // hidden, pending request aborted).
  function enforceMinLength(drawer) {
    if (!drawer.delegate || !drawer._debounce) return;
    drawer.delegate.off("input", '[name="q"]');
    drawer.delegate.on("input", '[name="q"]', drawer._debounce(function (event, target) {
      var value = target.value.trim().length >= MIN_QUERY_LENGTH ? target.value : "";
      return this._onSearch(event, { value: value });
    }, SEARCH_DEBOUNCE_MS));
  }

  var PredictiveSearchDrawer = window.customElements.get("predictive-search-drawer");
  if (PredictiveSearchDrawer) {
    // Drawers connected later (theme editor section reloads)
    var connectedCallback = PredictiveSearchDrawer.prototype.connectedCallback;
    PredictiveSearchDrawer.prototype.connectedCallback = function () {
      connectedCallback.call(this);
      enforceMinLength(this);
    };
    // Drawers already connected when theme.js defined the element
    document.querySelectorAll("predictive-search-drawer").forEach(enforceMinLength);
  }
})();
